import React, { useState } from 'react';
import { useAppDispatch } from '../../store/hooks';
import { addOrder, finalizeOrderWithInventoryCheck } from '../../store/slices/ordersSlice';
import { addClient } from '../../store/slices/clientsSlice';
import { generateMultipleTestOrdersWithClients } from '../../utils/generateTestOrders';

interface GenerateOrdersModalProps {
  isOpen: boolean;
  onClose: () => void;
  availableRecipes: Array<{ id: string; name: string }>;
}

const GenerateOrdersModal: React.FC<GenerateOrdersModalProps> = ({ isOpen, onClose, availableRecipes }) => {
  const dispatch = useAppDispatch();
  const [orderCount, setOrderCount] = useState<number>(10);
  const [error, setError] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState(false);

  if (!isOpen) return null;

  const handleGenerate = () => {
    // Validate input
    if (orderCount < 1 || orderCount > 100) {
      setError('Please enter a number between 1 and 100');
      return;
    }

    if (availableRecipes.length === 0) {
      setError('No products available. Please add recipes first.');
      return;
    }

    setError('');
    setIsGenerating(true);

    // Generate orders with clients
    try {
      const ordersWithClients = generateMultipleTestOrdersWithClients(orderCount, availableRecipes);

      // Add each client and order to the store, then finalize with inventory check
      ordersWithClients.forEach(({ order, client }) => {
        dispatch(addClient(client)); // Add client first
        dispatch(addOrder(order)); // Add order (not finalized)
        dispatch(finalizeOrderWithInventoryCheck({ orderId: order.id })); // Finalize with inventory check
      });

      // Close modal
      onClose();

      // Reset form
      setOrderCount(10);
    } catch (err) {
      setError('Failed to generate orders. Please try again.');
      console.error('Error generating orders:', err);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    setOrderCount(isNaN(value) ? 0 : value);
    setError('');
  };

  const handleClose = () => {
    if (!isGenerating) {
      setError('');
      setOrderCount(10);
      onClose();
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000
      }}
      onClick={handleClose}
    >
      <div
        style={{
          backgroundColor: 'white',
          padding: '32px',
          borderRadius: '8px',
          minWidth: '400px',
          maxWidth: '500px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
        }}
        onClick={e => e.stopPropagation()}
      >
        <h2 style={{ margin: '0 0 24px 0', color: '#333' }}>Generate Test Orders</h2>

        <p style={{ color: '#666', marginBottom: '24px', lineHeight: '1.5' }}>
          Generate random test orders with real Manitoba addresses, phone numbers, and customer data. Orders will be
          randomly distributed over the past 10 days.
        </p>

        <div style={{ marginBottom: '24px' }}>
          <label
            htmlFor="orderCount"
            style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: '500',
              color: '#333'
            }}
          >
            Number of Orders (1-100):
          </label>
          <input
            id="orderCount"
            type="number"
            min="1"
            max="100"
            value={orderCount || ''}
            onChange={handleInputChange}
            disabled={isGenerating}
            style={{
              width: '100%',
              padding: '12px',
              fontSize: '1rem',
              border: error ? '2px solid #f44336' : '1px solid #ddd',
              borderRadius: '4px',
              boxSizing: 'border-box'
            }}
          />
          {error && <p style={{ color: '#f44336', margin: '8px 0 0 0', fontSize: '0.9rem' }}>{error}</p>}
        </div>

        <div style={{ marginBottom: '24px', padding: '16px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
          <h3 style={{ margin: '0 0 12px 0', fontSize: '0.95rem', color: '#333' }}>Generated orders will include:</h3>
          <ul style={{ margin: 0, paddingLeft: '20px', color: '#666', fontSize: '0.9rem' }}>
            <li>Real Manitoba addresses (Winnipeg, Steinbach, and surrounding towns)</li>
            <li>Valid postal codes and 204 area phone numbers</li>
            <li>Realistic customer names and emails</li>
            <li>1-2 random products per order</li>
            <li>Random quantities and dates within last 10 days</li>
            <li>All orders start as "Pending" status</li>
          </ul>
        </div>

        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            onClick={handleClose}
            disabled={isGenerating}
            style={{
              padding: '12px 24px',
              backgroundColor: isGenerating ? '#ccc' : '#f5f5f5',
              color: '#333',
              border: 'none',
              borderRadius: '4px',
              cursor: isGenerating ? 'not-allowed' : 'pointer',
              fontSize: '1rem',
              fontWeight: '500'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleGenerate}
            disabled={isGenerating}
            style={{
              padding: '12px 24px',
              backgroundColor: isGenerating ? '#1976D2' : '#2196F3',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: isGenerating ? 'not-allowed' : 'pointer',
              fontSize: '1rem',
              fontWeight: 'bold',
              opacity: isGenerating ? 0.7 : 1
            }}
          >
            {isGenerating ? 'Generating...' : 'Generate Orders'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GenerateOrdersModal;
